import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { PulseLogo } from "@/components/site/Header";
import PageTransition from "@/components/site/PageTransition";

export function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (ok) navigate("/admin");
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center px-6 pt-24" data-testid="login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md p-10 rounded-3xl border border-white/10"
          style={{ background: "linear-gradient(180deg, #121215, #0c0c0e)", boxShadow: "0 30px 80px -20px rgba(0,184,255,0.18)" }}
        >
          <div className="flex flex-col items-center mb-8">
            <PulseLogo size={56} />
            <div className="font-display text-2xl font-bold tracking-tight mt-3">Pulse Media</div>
            <div className="label-mono mt-1">/ Sign In</div>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label-mono block mb-2">Email</label>
              <input
                className="pulse-input"
                type="email"
                required
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="label-mono block mb-2">Password</label>
              <input
                className="pulse-input"
                type="password"
                required
                autoComplete="current-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={password}
                onChange={(e) => setPw(e.target.value)}
                data-testid="login-password"
              />
            </div>
            {error && <div className="text-rose-400 text-sm" data-testid="login-error">{error}</div>}
            <button disabled={busy} className="glass-btn solid w-full justify-center" data-testid="login-submit">
              {busy ? "Signing in..." : "Sign In"}
            </button>
            <div className="flex items-center justify-between text-xs text-zinc-500 pt-2">
              <Link to="/forgot-password" className="link-blue" data-testid="login-forgot-link">Forgot password?</Link>
              <Link to="/register" className="link-blue">Create account</Link>
            </div>
          </form>
        </motion.div>
      </div>
    </PageTransition>
  );
}

export function Register() {
  const { register, error } = useAuth();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const invite = params.get("invite") || "";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await register({ name, email, password, invite_token: invite || undefined });
    setBusy(false);
    if (ok) navigate(invite ? "/admin" : "/");
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center px-6 pt-24" data-testid="register-page">
        <div className="w-full max-w-md p-10 rounded-3xl border border-white/10" style={{ background: "linear-gradient(180deg, #121215, #0c0c0e)" }}>
          <div className="flex flex-col items-center mb-8">
            <PulseLogo size={56} />
            <div className="font-display text-2xl font-bold tracking-tight mt-3">Create account</div>
            {invite && <div className="badge mt-2">Team Invite</div>}
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <input className="pulse-input" placeholder="Full name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name" />
            <input className="pulse-input" placeholder="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reg-email" />
            <input className="pulse-input" placeholder="Password (min 6)" type="password" required minLength={6} value={password} onChange={(e) => setPw(e.target.value)} data-testid="reg-password" />
            {error && <div className="text-rose-400 text-sm">{error}</div>}
            <button disabled={busy} className="glass-btn solid w-full justify-center" data-testid="reg-submit">
              {busy ? "Creating..." : "Create Account"}
            </button>
            <div className="text-center text-xs text-zinc-500 pt-2">
              Already have an account? <Link to="/login" className="link-blue">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </PageTransition>
  );
}
